if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.pointers = new Map();
      this.scale = 1;
      this.offset = { x: 0, y: 0 };
      this.maxScale = 5;
      this.addEventListener('click', this.onControlClick.bind(this));
      this.addEventListener('keydown', this.onKeyDown.bind(this));
      this.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.addEventListener('pointercancel', this.cancelGesture.bind(this));
      this.addEventListener('lostpointercapture', (event) => {
        if (this.pointers.has(event.pointerId)) this.cancelGesture();
      });
      this.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      this.addEventListener('dblclick', (event) => {
        if (!this.isImageStage(event.target) || Date.now() - (this.lastTouch || 0) < 700) return;
        event.preventDefault();
        this.zoomAt(this.scale > 1.01 ? 1 : 2.5, this.relativePoint(event));
      });
    }

    show(opener) {
      this.stage = this.querySelector('.product-media-modal__content');
      this.cancelGesture();
      const allItems = Array.from(this.stage.children).filter((item) => item.hasAttribute('data-media-id'));
      const gallery = document.getElementById(`MediaGallery-${this.dataset.gallerySection}`);
      const galleryIds = Array.from(gallery?.querySelectorAll('ul.product__media-list > li[data-media-id]') || [])
        .map((item) => item.dataset.mediaId.split('-').pop());
      const byId = new Map(allItems.map((item) => [item.dataset.mediaId, item]));
      this.items = galleryIds.length ? galleryIds.map((id) => byId.get(id)).filter(Boolean) : allItems;
      const requested = byId.get(opener.getAttribute('data-media-id'));
      if (requested && !this.items.includes(requested)) this.items.unshift(requested);
      allItems.forEach((item) => {
        item.classList.remove('active');
        item.setAttribute('aria-hidden', 'true');
        if (item.tagName === 'IMG') { item.draggable = false; item.sizes = '100vw'; }
      });
      super.show(opener);
      this.select(Math.max(0, this.items.indexOf(requested)));
      this.resizeObserver ||= new ResizeObserver(() => this.fitImage());
      this.resizeObserver.observe(this.stage);
    }

    hide() {
      this.resizeObserver?.disconnect();
      this.cancelGesture();
      this.lastTap = null;
      super.hide();
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) { this.resizeObserver?.disconnect(); cancelAnimationFrame(this.paintFrame); }
      });
    }

    select(index) {
      if (!this.items?.length) return;
      window.pauseAllMedia();
      this.cancelGesture();
      this.lastTap = null;
      this.index = (index + this.items.length) % this.items.length;
      this.items.forEach((item, i) => {
        item.classList.toggle('active', i === this.index);
        item.setAttribute('aria-hidden', String(i !== this.index));
      });
      this.activeMedia = this.items[this.index];
      this.image = this.activeMedia.tagName === 'IMG' ? this.activeMedia : null;
      this.scale = 1;
      this.offset = { x: 0, y: 0 };
      this.stage.classList.toggle('sh-gallery__stage--image', Boolean(this.image));
      this.stage.classList.remove('sh-gallery__stage--zoomed');
      this.querySelector('[data-gallery-counter]').textContent = `${this.index + 1} / ${this.items.length}`;
      this.querySelectorAll('[data-gallery-action="previous"], [data-gallery-action="next"]')
        .forEach((button) => { button.disabled = this.items.length < 2; });
      this.querySelector('[data-gallery-zoom]').hidden = !this.image;
      if (this.image) {
        this.image.loading = 'eager';
        this.image.addEventListener('load', () => this.fitImage(), { once: true });
        this.fitImage();
      } else if (this.activeMedia.querySelector('template')?.content.querySelector('.js-youtube')) {
        this.activeMedia.loadContent?.();
      }
    }

    fitImage() {
      if (!this.image || !this.hasAttribute('open')) return;
      const width = Number(this.image.getAttribute('width')) || this.image.naturalWidth;
      const height = Number(this.image.getAttribute('height')) || this.image.naturalHeight;
      if (!width || !height) return;
      this.area = { width: Math.max(1, this.stage.clientWidth - 32), height: Math.max(1, this.stage.clientHeight - 24) };
      const fit = Math.min(this.area.width / width, this.area.height / height, 1);
      this.fit = { width: width * fit, height: height * fit };
      this.image.style.width = `${this.fit.width}px`;
      this.image.style.height = `${this.fit.height}px`;
      this.clampOffset();
      this.paint();
    }

    clampOffset() {
      if (!this.fit || !this.area) return;
      const maxX = Math.max(0, (this.fit.width * this.scale - this.area.width) / 2);
      const maxY = Math.max(0, (this.fit.height * this.scale - this.area.height) / 2);
      this.offset.x = Math.max(-maxX, Math.min(maxX, this.offset.x));
      this.offset.y = Math.max(-maxY, Math.min(maxY, this.offset.y));
    }

    paint() {
      cancelAnimationFrame(this.paintFrame);
      this.paintFrame = requestAnimationFrame(() => {
        if (!this.image) return;
        this.image.style.transform = `translate3d(${this.offset.x}px, ${this.offset.y}px, 0) scale(${this.scale})`;
        this.stage.classList.toggle('sh-gallery__stage--zoomed', this.scale > 1.01);
        this.querySelector('[data-gallery-action="reset"]').textContent = `${Math.round(this.scale * 100)}%`;
        this.querySelector('[data-gallery-action="zoom-out"]').disabled = this.scale <= 1.001;
        this.querySelector('[data-gallery-action="zoom-in"]').disabled = this.scale >= this.maxScale - 0.001;
        this.image.sizes = `${Math.ceil(this.fit.width * this.scale)}px`;
      });
    }

    zoomAt(nextScale, point = { x: 0, y: 0 }) {
      if (!this.image || !this.fit) return;
      nextScale = Math.max(1, Math.min(this.maxScale, nextScale));
      const ratio = nextScale / this.scale;
      this.offset.x = point.x - (point.x - this.offset.x) * ratio;
      this.offset.y = point.y - (point.y - this.offset.y) * ratio;
      this.scale = nextScale;
      if (this.scale === 1) this.offset = { x: 0, y: 0 };
      this.clampOffset();
      this.paint();
    }

    relativePoint(point) {
      const bounds = this.stage.getBoundingClientRect();
      return { x: point.clientX - bounds.left - bounds.width / 2, y: point.clientY - bounds.top - bounds.height / 2 };
    }

    isImageStage(target) { return Boolean(this.image && this.stage?.contains(target)); }

    onControlClick(event) {
      const button = event.target.closest('[data-gallery-action]');
      if (!button || button.disabled) return;
      const action = button.dataset.galleryAction;
      if (action === 'previous') this.select(this.index - 1);
      if (action === 'next') this.select(this.index + 1);
      if (action === 'zoom-in') this.zoomAt(this.scale + 0.5);
      if (action === 'zoom-out') this.zoomAt(this.scale - 0.5);
      if (action === 'reset') this.zoomAt(1);
    }

    onKeyDown(event) {
      if (!this.hasAttribute('open') || event.target.closest('input, textarea, select, video, iframe, model-viewer, deferred-media, product-model')) return;
      const actions = {
        ArrowLeft: () => this.select(this.index - 1), ArrowRight: () => this.select(this.index + 1),
        '+': () => this.zoomAt(this.scale + 0.5), '=': () => this.zoomAt(this.scale + 0.5),
        '-': () => this.zoomAt(this.scale - 0.5), '0': () => this.zoomAt(1)
      };
      if (actions[event.key]) { event.preventDefault(); actions[event.key](); }
    }

    onWheel(event) {
      if (!this.isImageStage(event.target)) return;
      event.preventDefault();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.stage.clientHeight : 1);
      this.zoomAt(this.scale * Math.exp(-delta * 0.002), this.relativePoint(event));
    }

    onPointerDown(event) {
      if (!this.isImageStage(event.target) || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.pointerType === 'touch') this.lastTouch = Date.now();
      if (event.cancelable) event.preventDefault();
      this.stage.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
      if (this.pointers.size === 1) {
        this.gesture = { start: this.relativePoint(event), offset: { ...this.offset }, moved: false, pinched: false, scale: this.scale };
      } else if (this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        const middle = this.relativePoint({ clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 });
        this.gesture.pinched = true;
        this.gesture.moved = true;
        this.gesture.pinch = {
          distance: Math.max(1, Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)), scale: this.scale,
          anchor: { x: (middle.x - this.offset.x) / this.scale, y: (middle.y - this.offset.y) / this.scale }
        };
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      if (event.cancelable) event.preventDefault();
      this.pointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
      if (this.pointers.size >= 2 && this.gesture.pinch) {
        const [a, b] = Array.from(this.pointers.values());
        const middle = this.relativePoint({ clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 });
        const pinch = this.gesture.pinch;
        this.scale = Math.max(1, Math.min(this.maxScale, pinch.scale * Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) / pinch.distance));
        this.offset = { x: middle.x - pinch.anchor.x * this.scale, y: middle.y - pinch.anchor.y * this.scale };
      } else {
        const point = this.relativePoint(event);
        const dx = point.x - this.gesture.start.x, dy = point.y - this.gesture.start.y;
        if (Math.hypot(dx, dy) > 10) this.gesture.moved = true;
        if (this.scale <= 1.01) return;
        this.offset = { x: this.gesture.offset.x + dx, y: this.gesture.offset.y + dy };
      }
      this.clampOffset();
      this.paint();
    }

    onPointerUp(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      const gesture = this.gesture, point = this.relativePoint(event);
      this.pointers.delete(event.pointerId);
      if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
      if (this.pointers.size) {
        gesture.start = this.relativePoint(Array.from(this.pointers.values())[0]);
        gesture.offset = { ...this.offset }; gesture.pinch = null;
        return;
      }
      this.gesture = null;
      if (gesture.pinched) { this.lastTap = null; return; }
      const dx = point.x - gesture.start.x, dy = point.y - gesture.start.y;
      if (gesture.scale <= 1.01 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.select(this.index + (dx < 0 ? 1 : -1)); return;
      }
      if (gesture.moved || event.pointerType !== 'touch') return;
      const now = Date.now();
      if (this.lastTap && now - this.lastTap.time < 320 && Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < 32) {
        this.zoomAt(this.scale > 1.01 ? 1 : 2.5, point); this.lastTap = null;
      } else { this.lastTap = { ...point, time: now }; }
    }

    cancelGesture() {
      const ids = Array.from(this.pointers.keys());
      this.pointers.clear(); this.gesture = null;
      ids.forEach((id) => { if (this.stage?.hasPointerCapture(id)) this.stage.releasePointerCapture(id); });
    }
  });
}
